import type { ApolloError } from '@apollo/client';
import type { Classification } from '@seek/ca-myprofile-utils';
import { useTranslations } from '@vocab/react';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { useQuery, useToastMutation } from 'src/modules/apollo';
import { useAppConfig } from 'src/modules/appConfig';
import type { Maybe } from 'src/types';
import * as Schema from 'src/types/graphql';

import { useCandidateId } from '../../data';
import {
  selectClassification,
  selectClassificationFailure,
  selectClassificationSuccess,
} from '../actionCreators';

import translations from './.vocab';
import {
  generateUpdatePreferredClassificationOptimisticResponse,
  updateCacheUpdatePreferredClassification,
} from './apolloCache';

export const usePreferredClassification = () => {
  const { data, loading, error } = useQuery(
    Schema.GetPreferredClassificationDocument,
  );

  const isLoadingOrError = loading || error;

  const preferredClassification = isLoadingOrError
    ? null
    : data?.viewer?.preferredClassification || null;

  return { preferredClassification, loading, error };
};

const key = 'next-role-classification';

export const useUpdatePreferredClassification = () => {
  const dispatch = useDispatch();
  const { t } = useTranslations(translations);
  const { candidateId } = useCandidateId();

  const onMutate = () => {
    dispatch(selectClassification());
  };
  const onCompleted = () => {
    dispatch(selectClassificationSuccess());
  };
  const onError = (error: ApolloError) => {
    dispatch(selectClassificationFailure(error));
  };

  return useToastMutation(Schema.UpdatePreferredClassificationDocument, {
    key,
    errorActionRouteParameters: {
      to: 'classification',
      mode: 'EDIT',
    },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Classification of interest updated'),
    update: updateCacheUpdatePreferredClassification,
    optimisticResponse: generateUpdatePreferredClassificationOptimisticResponse(
      { _id: candidateId || '' },
    ),
  });
};

export const useGetClassificationOptions = () => {
  const { zone, language: languageCode } = useAppConfig();
  const { data, loading, error, refetch } = useQuery(
    Schema.GetClassificationOptionsDocument,
    {
      variables: { zone, languageCode },
    },
  );

  const classificationOptions = useMemo(
    () => data?.preferredClassificationOptions || [],
    [data],
  );

  const getSubClassifications = useCallback(
    (classificationId: number) => {
      const classificationList = classificationOptions.find(
        (item) => item.id === classificationId,
      );

      const subClassification = classificationList?.subClassifications || [];

      return subClassification.map((subClassificationItem) => ({
        id: subClassificationItem.id,
        name: subClassificationItem.description,
      }));
    },
    [classificationOptions],
  );

  const getClassificationText = useCallback(
    (classificationId: number, subClassificationId: number) => {
      const classification = classificationOptions.find(
        (item) => item.id === classificationId,
      );

      const subClassification = classification?.subClassifications?.find(
        (subItem) => subItem.id === subClassificationId,
      );

      return {
        classificationText: classification?.description || '',
        subClassificationText: subClassification?.description || '',
      };
    },
    [classificationOptions],
  );

  return {
    getSubClassifications,
    getClassificationText,
    classificationOptions,
    loading,
    error,
    refetch,
  };
};

/** Returns a mock response for use with the apollo MockedProvider */
export const mockGetPreferredClassificationRequest = (
  preferredClassification: Maybe<Classification> = null,
) => ({
  request: {
    query: Schema.GetPreferredClassificationDocument,
  },
  result: {
    data: {
      viewer: {
        _id: '1234',
        preferredClassification,
      },
    },
  },
});

export const mockGetClassificationOptionsRequest = (
  preferredClassificationOptions: Maybe<Schema.ClassificationOptions[]> = null,
) => ({
  request: {
    query: Schema.GetClassificationOptionsDocument,
    variables: { zone: 'anz-1', languageCode: 'en' },
  },
  result: {
    data: {
      preferredClassificationOptions,
    },
  },
});
